import { useAtomValue, useSetAtom } from "jotai";
import { createHostedChainProvider } from "./avalanche";
import type { ChainProvider } from "./provider";
import { chainAtom, feedStatusAtom, rpcThrottledAtom } from "../../state/atoms";
import type { Chain } from "../schemas";

/**
 * Scanner and Graduation each call this hook, but they must share one live feed
 * per chain rather than opening duplicate polling loops whenever tabs mount.
 * The browser only sees sanitized same-origin snapshots.
 */
let cache: { key: Chain; provider: ChainProvider } | null = null;

function getSharedProvider(
  chain: Chain,
  onStatus: (s: "LIVE" | "STALE" | "DEGRADED" | "UNAVAILABLE") => void,
  onThrottle: (t: boolean) => void,
): ChainProvider {
  if (cache && cache.key === chain) return cache.provider;
  cache?.provider.dispose?.();
  onStatus("UNAVAILABLE");
  onThrottle(false);
  const provider: ChainProvider = createHostedChainProvider(chain, { onStatus, onThrottle });
  cache = { key: chain, provider };
  return provider;
}

/** One shared same-origin hosted provider per selected chain. */
export function useChainProvider(): ChainProvider {
  const chain = useAtomValue(chainAtom);
  const setFeedStatus = useSetAtom(feedStatusAtom);
  const setThrottled = useSetAtom(rpcThrottledAtom);

  return getSharedProvider(chain, setFeedStatus, setThrottled);
}
